import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, X, Search, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ExcelJS from 'exceljs';
import { useAuth } from "@/hooks/useAuth";
const normalizeName = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ExcelRow {
  Date: string;
  Description: string;
  Value: number;
  Account: string;
  Status: string;
  Category: string;
}

interface TransactionPreview {
  data: string;
  descricao: string;
  valor: number;
  tipo: 'entrada' | 'saida';
  conta_nome: string;
  categoria_nome: string;
  foi_paga: boolean;
}

interface ImportResult {
  success: number;
  errors: string[];
  duplicates: number;
  duplicateDetails: TransactionPreview[];
}

export function ImportTransactions({ onImportSuccess }: { onImportSuccess?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<TransactionPreview[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [removedTransactions, setRemovedTransactions] = useState<Set<number>>(new Set());
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const { user } = useAuth();

  // Filtrar e gerenciar transações
  const filteredPreview = useMemo(() => {
    return preview
      .map((transaction, index) => ({ ...transaction, originalIndex: index }))
      .filter((transaction) => {
        // Filtrar transações removidas
        if (removedTransactions.has(transaction.originalIndex)) {
          return false;
        }
        
        // Filtrar por termo de busca
        if (searchTerm.trim() === "") {
          return true;
        }
        
        const searchLower = searchTerm.toLowerCase();
        return (
          transaction.descricao.toLowerCase().includes(searchLower) ||
          transaction.conta_nome.toLowerCase().includes(searchLower) ||
          transaction.categoria_nome.toLowerCase().includes(searchLower)
        );
      });
  }, [preview, searchTerm, removedTransactions]);

  const removeTransaction = (originalIndex: number) => {
    setRemovedTransactions(prev => new Set([...prev, originalIndex]));
  };

  const resetFiltersAndRemovals = () => {
    setSearchTerm("");
    setRemovedTransactions(new Set());
  };

  const validateExcelStructure = (data: ExcelRow[]): boolean => {
    if (!data || data.length === 0) return false;
    
    const requiredColumns = ['Date', 'Description', 'Value', 'Account', 'Status', 'Category'];
    const firstRow = data[0];
    
    return requiredColumns.every(col => col in firstRow);
  };

  const parseDate = (dateStr: string): string => {
    try {
      // Tentar vários formatos de data
      if (dateStr.includes('/')) {
        const [day, month, year] = dateStr.split('/');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toISOString();
      } else if (dateStr.includes('-')) {
        return new Date(dateStr).toISOString();
      }
      // Se for um número do Excel (data serial)
      if (!isNaN(Number(dateStr))) {
        const excelDate = new Date((Number(dateStr) - 25569) * 86400 * 1000);
        return excelDate.toISOString();
      }
      throw new Error('Formato de data inválido');
    } catch (error) {
      throw new Error(`Data inválida: ${dateStr}`);
    }
  };

  const isValidTransactionRow = (row: ExcelRow): boolean => {
    // Filtrar linhas de total, cabeçalhos ou outras linhas inválidas
    if (!row.Date || !row.Description || row.Value === undefined || row.Value === null) {
      return false;
    }
    
    const dateStr = row.Date.toString().toLowerCase();
    const descStr = row.Description.toString().toLowerCase();
    
    // Filtrar linhas de total ou resumo
    if (dateStr.includes('total') || 
        descStr.includes('total') ||
        dateStr.includes('summary') ||
        descStr.includes('summary') ||
        dateStr === 'date' || // Cabeçalho duplicado
        descStr === 'description') { // Cabeçalho duplicado
      return false;
    }
    
    return true;
  };

  const processExcelData = useCallback(async (excelData: ExcelRow[]): Promise<TransactionPreview[]> => {
    const processed: TransactionPreview[] = [];
    
    // Filtrar apenas linhas válidas
    const validRows = excelData.filter(isValidTransactionRow);
    
    for (const row of validRows) {
      try {
        const valor = Math.abs(Number(row.Value));
        const tipo: 'entrada' | 'saida' = Number(row.Value) >= 0 ? 'entrada' : 'saida';
        
        processed.push({
          data: parseDate(row.Date.toString()),
          descricao: row.Description || '',
          valor: valor,
          tipo: tipo,
          conta_nome: row.Account || 'Carteira',
          categoria_nome: row.Category || 'Outros',
          foi_paga: row.Status !== 'Pending'
        });
      } catch (error) {
        console.error('Erro ao processar linha:', row, error);
        // Continue processando outras linhas em vez de parar completamente
        continue;
      }
    }
    
    return processed;
  }, []);

  const processFile = useCallback(async (uploadedFile: File) => {
    if (!uploadedFile.name.endsWith('.xlsx')) {
      toast.error("Por favor, selecione um arquivo .xlsx");
      return;
    }

    setFile(uploadedFile);
    setIsLoading(true);
    setProgress(10);

    try {
      const arrayBuffer = await uploadedFile.arrayBuffer();
      setProgress(30);

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('O arquivo Excel não contém nenhuma planilha.');
      }

      const jsonData: ExcelRow[] = [];
      const headers: string[] = [];
      
      // Ler cabeçalhos da primeira linha
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell, colNumber) => {
        headers[colNumber] = cell.value ? cell.value.toString() : '';
      });

      // Ler dados das linhas subsequentes
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Pular cabeçalho
        
        const rowData: Record<string, unknown> = {};
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber];
          if (header) {
            let value = cell.value;
            // Tratar objetos complexos do ExcelJS (fórmulas, links)
            if (value && typeof value === 'object') {
              if ('result' in value) value = (value as Record<string, unknown>).result;
              else if ('text' in value) value = (value as Record<string, unknown>).text;
            }
            rowData[header] = value;
          }
        });
        jsonData.push(rowData as unknown as ExcelRow);
      });
      
      setProgress(50);

      if (!validateExcelStructure(jsonData)) {
        throw new Error('Estrutura do arquivo inválida. Colunas obrigatórias: Date, Description, Value, Account, Status, Category');
      }

      setProgress(70);
      const processedData = await processExcelData(jsonData);
      setProgress(100);

      setPreview(processedData);
      setShowPreview(true);
      
      toast.success(`${processedData.length} transações encontradas`);

    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      toast.error(error instanceof Error ? error.message : "Erro desconhecido");
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [processExcelData]);

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;
    await processFile(uploadedFile);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (isLoading) return;
    
    const files = Array.from(e.dataTransfer.files);
    const uploadedFile = files[0];
    
    if (uploadedFile && uploadedFile.name.endsWith('.xlsx')) {
      await processFile(uploadedFile);
    } else if (uploadedFile) {
      toast.error("Por favor, selecione apenas arquivos .xlsx");
    }
  }, [processFile, isLoading]);

  const findOrCreateCategory = async (categoryName: string, tipo: 'entrada' | 'saida'): Promise<string> => {
    const normalizedName = categoryName.trim();
    const normalizedKey = normalizeName(normalizedName);
    const rpcCall = (supabase as unknown as { rpc: (...args: unknown[]) => unknown }).rpc(
      'find_categoria_by_normalized',
      {
        p_user_id: user!.id,
        p_nome_normalizado: normalizedKey,
        p_tipo: tipo
      }
    ) as unknown as { single: () => Promise<{ data: { id: string } | null }> };
    const existingCategoryResp = await rpcCall.single();
    const existingCategory = existingCategoryResp.data;

    if (existingCategory) {
      return existingCategory.id;
    }

    const { data: newCategory, error } = await supabase
      .from('categorias')
      .insert({
        nome: normalizedName,
        tipo: tipo === 'entrada' ? 'entrada' : 'saida',
        cor: '#3B82F6'
      })
      .select('id')
      .single();

    if (error) throw error;
    return newCategory.id;
  };

  const findOrCreateAccount = async (accountName: string): Promise<string> => {
    // Buscar conta existente
    const { data: existingAccount } = await supabase
      .from('contas')
      .select('id, tipo')
      .eq('nome', accountName)
      .single();

    if (existingAccount) {
      return existingAccount.id;
    }

    // Não criar contas automaticamente para cartões de crédito
    // O usuário deve criar cartões de crédito manualmente através da interface específica
    // Criar apenas contas do tipo 'carteira' automaticamente
    const { data: newAccount, error } = await supabase
      .from('contas')
      .insert({
        nome: accountName,
        tipo: 'carteira',
        cor: '#3B82F6',
        saldo_inicial: 0
      })
      .select('id')
      .single();

    if (error) throw error;
    return newAccount.id;
  };

  const executeImport = async () => {
    if (!preview.length || !user) return;

    setIsLoading(true);
    setProgress(0);
    
    const result: ImportResult = {
      success: 0,
      errors: [],
      duplicates: 0,
      duplicateDetails: [],
    };

    // Filtrar transações não removidas
    const transactionsToImport = preview.filter((_, index) => !removedTransactions.has(index));
    
    if (transactionsToImport.length === 0) {
      toast.error("Nenhuma transação selecionada para importação");
      setIsLoading(false);
      return;
    }

    try {
      for (let i = 0; i < transactionsToImport.length; i++) {
        const transaction = transactionsToImport[i];
        setProgress((i / transactionsToImport.length) * 100);

        try {
          // Verificar duplicatas
          const { data: existing } = await supabase
            .from('transacoes')
            .select('id, descricao, valor, data')
            .eq('descricao', transaction.descricao)
            .eq('valor', transaction.valor)
            .eq('data', transaction.data)
            .eq('user_id', user.id);

          if (existing && existing.length > 0) {
            result.duplicates++;
            result.duplicateDetails.push(transaction);
            console.log('Duplicata encontrada:', transaction);
            continue;
          }

          // Buscar/criar categoria e conta
          const categoriaId = await findOrCreateCategory(transaction.categoria_nome, transaction.tipo);
          const contaId = await findOrCreateAccount(transaction.conta_nome);

          // Inserir transação
          const { error } = await supabase
            .from('transacoes')
            .insert({
              user_id: user.id,
              tipo: transaction.tipo,
              valor: transaction.valor,
              descricao: transaction.descricao,
              data: transaction.data,
              categoria_id: categoriaId,
              conta_id: contaId,
              foi_paga: transaction.foi_paga,
              data_pagamento: transaction.foi_paga ? transaction.data : null,
              despesa_fixa: false,
              repetir: false,
              ignorar_transacao: false
            });

          if (error) throw error;
          result.success++;

        } catch (error) {
          console.error('Erro ao importar transação:', error);
          result.errors.push(`${transaction.descricao}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      setProgress(100);
      setImportResult(result);
      
      // Mostrar diálogo com resultado da importação
      setShowResultDialog(true);

      // Não fechar automaticamente - deixar o usuário decidir
      // if (onImportSuccess) {
      //   onImportSuccess();
      // }

    } catch (error) {
      console.error('Erro durante importação:', error);
      // Erro será mostrado na tela de resultados, não precisa de toast
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview([]);
    setShowPreview(false);
    setImportResult(null);
    setProgress(0);
    setShowResultDialog(false);
    resetFiltersAndRemovals();
  };

  const renderImportResult = () => {
    if (!importResult) return null;
    
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {importResult.success > 0 ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                Importação Concluída com Sucesso
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Importação Concluída com Avisos
              </>
            )}
          </CardTitle>
          <CardDescription>
            Resumo detalhado da importação de transações
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>{importResult.success}</strong> transações importadas
              </AlertDescription>
            </Alert>
            
            {importResult.duplicates > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{importResult.duplicates}</strong> duplicatas ignoradas
                </AlertDescription>
              </Alert>
            )}
            
            {importResult.errors.length > 0 && (
              <Alert variant="destructive">
                <X className="h-4 w-4" />
                <AlertDescription>
                  <strong>{importResult.errors.length}</strong> erros encontrados
                </AlertDescription>
              </Alert>
            )}
          </div>

          {importResult.duplicateDetails && importResult.duplicateDetails.length > 0 && (
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Transações Duplicadas Ignoradas ({importResult.duplicateDetails.length})
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  As seguintes transações já existem no sistema e foram ignoradas durante a importação:
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="p-3 text-left font-medium">Data</th>
                          <th className="p-3 text-left font-medium">Descrição</th>
                          <th className="p-3 text-left font-medium">Valor</th>
                          <th className="p-3 text-left font-medium">Conta</th>
                          <th className="p-3 text-left font-medium">Categoria</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.duplicateDetails.map((t, index) => (
                          <tr key={index} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="p-3">{new Date(t.data).toLocaleDateString('pt-BR')}</td>
                            <td className="p-3 max-w-xs truncate" title={t.descricao}>{t.descricao}</td>
                            <td className="p-3 font-medium">
                              <span className={t.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}>
                                {t.tipo === 'entrada' ? '+' : '-'} R$ {Math.abs(t.valor).toFixed(2)}
                              </span>
                            </td>
                            <td className="p-3">{t.conta_nome}</td>
                            <td className="p-3">{t.categoria_nome}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {importResult.errors.length > 0 && (
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <X className="h-5 w-5 text-red-600" />
                  Erros Encontrados ({importResult.errors.length})
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Os seguintes erros impediram a importação de algumas transações:
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {importResult.errors.map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                      <span className="font-medium">Erro {index + 1}:</span> {error}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={resetImport} className="flex-1">
              Importar Novo Arquivo
            </Button>
            {onImportSuccess && (
              <Button 
                variant="outline" 
                onClick={() => {
                  onImportSuccess();
                }} 
                className="flex-1"
              >
                Fechar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderPreview = () => {
    if (!showPreview) return null;
    
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Preview da Importação</CardTitle>
          <CardDescription>
            {preview.length} transações encontradas. {removedTransactions.size > 0 && `${removedTransactions.size} removidas.`} Revise os dados antes de confirmar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campo de Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por descrição, conta ou categoria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Estatísticas */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span>Total: {preview.length} transações</span>
            <span>Exibindo: {filteredPreview.length} transações</span>
            {removedTransactions.size > 0 && (
              <span className="text-red-600">Removidas: {removedTransactions.size}</span>
            )}
            {removedTransactions.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFiltersAndRemovals}
                className="h-auto p-0 text-blue-600 hover:text-blue-800"
              >
                Restaurar todas
              </Button>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block max-h-96 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="p-2 text-left">Data</th>
                  <th className="p-2 text-left">Descrição</th>
                  <th className="p-2 text-left">Tipo</th>
                  <th className="p-2 text-left">Valor</th>
                  <th className="p-2 text-left">Conta</th>
                  <th className="p-2 text-left">Categoria</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left w-16">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredPreview.map((transaction) => (
                  <tr key={transaction.originalIndex} className="border-b">
                    <td className="p-2">{new Date(transaction.data).toLocaleDateString('pt-BR')}</td>
                    <td className="p-2">{transaction.descricao}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        transaction.tipo === 'entrada' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {transaction.tipo === 'entrada' ? 'Receita' : 'Despesa'}
                      </span>
                    </td>
                    <td className="p-2">R$ {transaction.valor.toFixed(2)}</td>
                    <td className="p-2">{transaction.conta_nome}</td>
                    <td className="p-2">{transaction.categoria_nome}</td>
                    <td className="p-2">
                      {transaction.foi_paga ? 'Pago' : 'Pendente'}
                    </td>
                    <td className="p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTransaction(transaction.originalIndex)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                        title="Remover transação"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden max-h-96 overflow-y-auto space-y-3">
            {filteredPreview.map((transaction) => (
              <div key={transaction.originalIndex} className="border rounded-lg p-3 space-y-2 bg-card">
                <div className="flex justify-between items-start">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{transaction.descricao}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(transaction.data).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                      transaction.tipo === 'entrada' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {transaction.tipo === 'entrada' ? 'Receita' : 'Despesa'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTransaction(transaction.originalIndex)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50 flex-shrink-0"
                      title="Remover transação"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Valor:</span>
                    <p className="font-medium">R$ {transaction.valor.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <p className="font-medium">{transaction.foi_paga ? 'Pago' : 'Pendente'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Conta:</span>
                    <p className="font-medium truncate">{transaction.conta_nome}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Categoria:</span>
                    <p className="font-medium truncate">{transaction.categoria_nome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {isLoading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Importando transações...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button 
              onClick={executeImport}
              disabled={isLoading || preview.length - removedTransactions.size === 0}
              className="flex-1 w-full sm:w-auto"
            >
              {isLoading ? 'Importando...' : `Confirmar Importação (${preview.length - removedTransactions.size} transações)`}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowPreview(false)}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Voltar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  function ResultDialog() {
    if (!importResult) return null;
    
    return (
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult.success > 0 ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Importação Concluída com Sucesso
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Importação Concluída com Avisos
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Resumo detalhado da importação de transações
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{importResult.success}</strong> transações importadas
                </AlertDescription>
              </Alert>
              
              {importResult.duplicates > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{importResult.duplicates}</strong> duplicatas ignoradas
                  </AlertDescription>
                </Alert>
              )}
              
              {importResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <X className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{importResult.errors.length}</strong> erros encontrados
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {importResult.duplicateDetails && importResult.duplicateDetails.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Transações duplicadas ignoradas:</h4>
                <div className="max-h-60 overflow-y-auto space-y-1 border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Data</th>
                        <th className="p-2 text-left">Descrição</th>
                        <th className="p-2 text-left">Valor</th>
                        <th className="p-2 text-left">Conta</th>
                        <th className="p-2 text-left">Categoria</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.duplicateDetails.map((t, index) => (
                        <tr key={index} className="border-b hover:bg-muted/50">
                          <td className="p-2">{new Date(t.data).toLocaleDateString('pt-BR')}</td>
                          <td className="p-2">{t.descricao}</td>
                          <td className="p-2 font-medium">
                            <span className={t.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}>
                              R$ {t.valor.toFixed(2)}
                            </span>
                          </td>
                          <td className="p-2">{t.conta_nome}</td>
                          <td className="p-2">{t.categoria_nome}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importResult.errors.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Erros detalhados:</h4>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {importResult.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowResultDialog(false)}
            className="flex-1"
          >
            Continuar Importando
          </Button>
          {onImportSuccess && (
            <Button 
              onClick={() => {
                setShowResultDialog(false);
                onImportSuccess();
              }}
              className="flex-1"
            >
              Fechar Importação
            </Button>
          )}
        </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const renderMainContent = () => (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6" />
          Importar Transações
        </CardTitle>
        <CardDescription>
          Faça upload de um arquivo Excel (.xlsx) com suas transações para importá-las automaticamente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="text-sm space-y-2">
            <h4 className="font-medium">Formato esperado do arquivo:</h4>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li><strong>Date:</strong> Data da transação (dd/mm/yyyy ou yyyy-mm-dd)</li>
              <li><strong>Description:</strong> Descrição da transação</li>
              <li><strong>Value:</strong> Valor (positivo para receitas, negativo para despesas)</li>
              <li><strong>Account:</strong> Nome da conta (ex: Carteira, Banco)</li>
              <li><strong>Status:</strong> Status da transação ("Pending" = pendente, "Paid" = paga)</li>
              <li><strong>Category:</strong> Categoria da transação</li>
            </ul>
          </div>

          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragOver 
                ? 'border-primary bg-primary/5 border-solid' 
                : 'border-muted-foreground/25'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              disabled={isLoading}
            />
            <label 
              htmlFor="file-upload" 
              className="cursor-pointer flex flex-col items-center space-y-4"
            >
              <Upload className={`h-12 w-12 ${
                isDragOver ? 'text-primary' : 'text-muted-foreground'
              }`} />
              <div>
                <div className="text-base font-medium">
                  {isDragOver 
                    ? 'Solte o arquivo aqui' 
                    : file 
                      ? file.name 
                      : 'Clique para selecionar ou arraste um arquivo'
                  }
                </div>
                <div className="text-sm text-muted-foreground">
                  Apenas arquivos .xlsx são aceitos
                </div>
              </div>
            </label>
          </div>

          {isLoading && !showPreview && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processando arquivo...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {file && !isLoading && !showPreview && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Arquivo carregado com sucesso. Os dados foram validados e estão prontos para importação.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
  
  return (
    <>
      {importResult ? renderImportResult() : showPreview ? renderPreview() : renderMainContent()}
      {importResult && showResultDialog ? <ResultDialog /> : null}
    </>
  );
}
